import { useCallback, useEffect, useRef, useState } from "react";

import { get as commsGet } from "../../../service/comms/commsApi";
import { listRecipients } from "../../../service/comms/broadcastsService";
import { recipientsFromPayload } from "./broadcastDecode";
import { messageFor } from "./broadcastErrors";

/** Debounce for the free-text search, in ms. Typing, unlike a pill, is bursty. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Recipients for one broadcast, for both the Recipients and Engagement tabs.
 *
 * The two tabs hit the SAME endpoint with different parameters — `state=` for
 * delivery, `engagement=` for behaviour — so they share one hook rather than
 * duplicating the fetch, the debounce and the race guard twice over.
 *
 * @param {string} id - Broadcast id.
 * @param {{ state?: string, engagement?: string, query?: string, enabled?: boolean }} params
 * @returns {{ recipients: Object[], total: number|null, loading: boolean, error: string|null }}
 */
export default function useBroadcastRecipients(
  id,
  { state, engagement, query = "", enabled = true } = {}
) {
  const [recipients, setRecipients] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generation counter plus the effect's own cleanup flag. The flag alone is
  // not enough: the debounce means a request can outlive the effect that
  // scheduled it, and a slow first response must never overwrite a fast second.
  const generation = useRef(0);

  const trimmedQuery = query.trim();

  const load = useCallback(
    (cancelled) => {
      const mine = ++generation.current;
      const isStale = () => cancelled?.value || mine !== generation.current;

      setLoading(true);
      listRecipients(commsGet, id, {
        state,
        engagement,
        q: trimmedQuery || undefined,
      })
        .then((payload) => {
          if (isStale()) return;
          const decoded = recipientsFromPayload(payload);
          setRecipients(decoded.recipients);
          setTotal(decoded.total);
          setError(null);
        })
        .catch((err) => {
          if (isStale()) return;
          setRecipients([]);
          setTotal(null);
          setError(messageFor(err));
        })
        .finally(() => {
          if (isStale()) return;
          setLoading(false);
        });
    },
    [id, state, engagement, trimmedQuery]
  );

  useEffect(() => {
    if (!id || !enabled) return undefined;

    const cancelled = { value: false };
    // Only the free-text query is debounced. A pill click is a discrete
    // decision and should feel immediate; a keystroke is not.
    const timer = setTimeout(() => load(cancelled), trimmedQuery ? SEARCH_DEBOUNCE_MS : 0);

    return () => {
      cancelled.value = true;
      clearTimeout(timer);
    };
  }, [id, enabled, load, trimmedQuery]);

  return { recipients, total, loading, error };
}
